import {useEffect, useState} from "react";
import {apiDelete, apiGet, apiPost, apiPut, extractPayloadFromResponse} from "../../services/apiService";
import {useAuth} from "../../providers/AuthProvider";
import {useSiteMessage} from "../../providers/SiteMessageProvider";
import {HashTags} from "@varymadellc/my-weed-club-component-library";
import cx from "classnames";
import Loading from "@/components/Loading/Loading";

export interface HashTagsCollectionProps {
    uuid: string;
    className?: string;
    hideTitle?: boolean;
    tagAddedCallback?: (collectionUuid: string, tagUuid: string) => void;
}

interface Tag {
    uuid: string;
    name: string;
    link?: string;
}

export default function HashTagsCollection({uuid, className, hideTitle = false, tagAddedCallback}: HashTagsCollectionProps) {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [refresh, setRefresh] = useState<number>(0);

    const [hashTags, setHashTags] = useState<Tag[]>([]);

    const {setMessage} = useSiteMessage();

    const {authToken, user} = useAuth();

    useEffect(() => {
        async function getData() {
            try {
                const response = await apiGet(`/tags/${uuid}`, authToken);

                const payload = await extractPayloadFromResponse(response, true);

                const {error, data} = payload;

                if (error) {
                    console.log(error);

                    return;
                }

                const tags = data?.tags.map((tag: any) => {
                    return {
                        uuid: tag.uuid,
                        name: tag.name,
                        link: `/u/${tag.AccountUuid}`,
                        onDeleteCallback: (user.uuid === uuid) ? async () => {
                            await deleteTagHandler(uuid, tag.uuid);
                        } : undefined
                    }
                }) || [];

                setHashTags(tags);
            } catch (error: any) {
                setMessage(error.message);
            }
        }

        getData();
    }, [refresh]);

    const deleteTagHandler = async (collectionUuid, tagUuid) => {
        try {
            const confirm = window.confirm(`Are you sure you want to delete the tag ${name}?`);

            if (!confirm) {
                return;
            }

            setIsLoading(true);

            const response = await apiDelete(`/tags/tag`, authToken, undefined, JSON.stringify({
                userUuid: user.uuid,
                tagUuid,
                collectionUuid
            }));

            const {error, data} = await extractPayloadFromResponse(response);

            setIsLoading(false);

            if (error) {
                setMessage(error);
                return;
            }

            if (data === 'deleted') {
                setRefresh(refresh + 1);
                setMessage('Tag deleted');
                return;
            }

            setMessage('An issue occurred, please try again later.');
        } catch (error: any) {
            setIsLoading(false);

            setMessage(error.message);
        }
    };

    const addTagHandler = async (uuid, name) => {
        try {
            setIsLoading(true);

            const response = await apiPost(`/tag`, JSON.stringify({
                name,
            }), authToken);

            const {error, data} = await extractPayloadFromResponse(response);

            if (error) {
                throw new Error(error);
            }

            if (data) {
                const tagsResponse = await apiPut(`/tags/${uuid}`, JSON.stringify({
                    tags: [data.uuid],
                }), authToken);

                const {error: tagsError, data: tagsData} = await extractPayloadFromResponse(tagsResponse);

                if (tagsError) {
                    console.log(tagsError);
                    return;
                }

                setRefresh(refresh + 1);

                setMessage('Tag added');

                tagAddedCallback && tagAddedCallback(uuid, data.uuid);

                setIsLoading(false);

                return;
            }

            setIsLoading(false);

            setMessage('An issue occurred, please try again later.');
        } catch (error: any) {
            setIsLoading(false);

            setMessage(error.message);
        }
    };

    return (
        <div className={cx('rounded h-full', className )}>
            {isLoading && <Loading hasOverlay={true}/>}
            <HashTags hideTitle={hideTitle}  addTag={addTagHandler} uuid={uuid} tags={hashTags}/>
        </div>
    );
}